import type { Asset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { ABSENT, UNREGISTERED } from '../constants';

import { IconRegistry } from 'js/src/agent/registries/IconRegistry';
import { getColorLabel } from 'js/src/agent/utils';
import { intToHex } from 'js/src/libs/color-utils';

/**
 * Render a color as agent-facing hex, adding the color name only when it's a close match (LLMs read
 * hex far better than a 24-bit decimal, and a nearest-name for an off-palette color is misleading).
 * Example: "#ffffff (White)" for a named color, "#3a7bc2" for an off-palette one. When
 * `includeColorNames` is false the name is dropped entirely and only the hex is emitted — used by
 * agents whose models read hex directly and don't want the extra token (the Toolbox).
 */
export const renderColorAsString = (color: number, includeColorNames: boolean = true): string => {
    const hex = intToHex(color);
    if (!includeColorNames) {
        return hex;
    }
    const label = getColorLabel(color);
    return label ? `${hex} (${label})` : hex;
};

/**
 * The template colour slot an asset's colour property points at, rendered as ` [slot N]`, or an
 * empty string when the colour is a literal rather than a template reference.
 *
 * The resolved hex alone cannot express INTENT: an outro whose background references slot 0 and a
 * body scene that happens to be the same colour serialize identically, so an agent imitating the
 * video has no way to see that the outro deliberately inverts the palette. The slot is also what
 * `set_template_color` addresses, so naming it tells the agent which dial produced the look.
 */
export const renderColorSlot = (asset: Asset, propertyPath: string): string => {
    const index = VideoData.getTemplateIndexFromAssetProperty(asset, propertyPath);
    return index >= 0 ? ` [slot ${index}]` : '';
};

/**
 * Reverse-map an icon asset to its IconRegistry id — the same id `set_icon` accepts as `media_id`.
 * Mirrors getMediaIdFromAsset for media assets, so the serialized icon carries the handle the agent
 * needs to see which icon is set (and to diff icon swaps). Returns ABSENT when the asset isn't an
 * icon or has no source uuid, and UNREGISTERED when its uuid isn't in the registry yet (e.g. a
 * template default the agent hasn't searched up).
 */
export const getIconIdFromAsset = (asset: Asset): string => {
    if (!VideoData.isIconAsset(asset)) {
        return ABSENT;
    }
    const uuid = asset.sourceData?.uuid;
    if (!uuid) {
        return ABSENT;
    }
    return IconRegistry.getInstance().getItemIdByUuid(uuid) || UNREGISTERED;
};

/**
 * Format icon color maps into a readable string.
 * Returns a comma-separated list of colors with "and" for the last color.
 * Example: "#ffffff (White), #000000 (Black) and #ff0000 (Red)"
 */
export const formatIconColors = (asset: Asset, includeColorNames: boolean = true): string => {
    if (!VideoData.isIconAsset(asset)) {
        return '';
    }

    let colors = '';
    asset.colorMaps.forEach((colorMap, index, array) => {
        if (colors) {
            if (index === array.length - 1) {
                colors += ' and ';
            } else {
                colors += ', ';
            }
        }
        colors += Number.isFinite(colorMap.targetColor)
            ? renderColorAsString(colorMap.targetColor, includeColorNames)
            : ABSENT;
    });

    return colors;
};
